import React, { useState, useEffect } from "react";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, Divider, Toolbar, IconButton, TextField, Button, Avatar, Collapse, Tooltip } from "@mui/material";
import { Inbox, Send, Drafts, Delete, Report, Archive, Search, Refresh, MarkEmailRead, MarkEmailUnread, Flag, OutlinedFlag, Reply, ReplyAll, Forward, PushPin, PushPinOutlined, DeleteOutline, ExpandLess, ExpandMore, Menu as MenuIcon, Add as AddIcon } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SetMailReplyPayload } from "../../Redux/action/EmailAction";
import { basicurl } from "../../Redux/ServiceUrl/url";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 68;
const EMAIL_LIST_WIDTH = 380;
// Outlook shows image attachments as a small thumbnail, not at full width.
const ATTACHMENT_PREVIEW_SIZE = 180;

const downloadAttachment = (att) => {
  if (!att?.contentBytes) return;
  const link = document.createElement("a");
  link.href = `data:${att.contentType};base64,${att.contentBytes}`;
  link.download = att.name;
  link.click();
};

function Webmail() {
  const [selectedFolder, setSelectedFolder] = useState("Inbox");
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailDetails, setEmailDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinnedExpanded, setPinnedExpanded] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  
  const token = window.sessionStorage.getItem("user_token");
  const msAuthToken = window.sessionStorage.getItem("ms_auth");
  const API_URL = basicurl;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const folders = [
    { name: "Inbox", icon: <Inbox />, graphName: "Inbox" },
    { name: "Sent Items", icon: <Send />, graphName: "SentItems" },
    { name: "Drafts", icon: <Drafts />, graphName: "Drafts" },
    { name: "Archive", icon: <Archive />, graphName: "Archive" },
    { name: "Junk Email", icon: <Report />, graphName: "JunkEmail" },
    { name: "Deleted Items", icon: <Delete />, graphName: "DeletedItems" }
  ];

  const parseSenderName = (fromStr) => {
    if (!fromStr) return "Unknown";
    // If it's an Exchange Legacy DN
    if (fromStr.toUpperCase().includes("/O=EXCHANGELABS") || fromStr.toUpperCase().includes("/OU=")) {
      const parts = fromStr.split("cn=");
      if (parts.length > 1) {
        let name = parts[parts.length - 1].trim();
        // Remove trailing hex IDs sometimes appended by Exchange
        name = name.replace(/-[0-9a-fA-F]{10,}$/i, '');
        return name || "Exchange User";
      }
      return "Exchange User";
    }
    // If it's a regular email
    if (fromStr.includes("@")) {
      const prefix = fromStr.split("@")[0];
      return prefix.split(/[._-]/).map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(" ");
    }
    return fromStr;
  };

  const fetchEmails = async (folderName, search = "") => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mail/fetchFolderMailMessage`, {
        headers: { authorization: `Bearer ${token}` },
        params: { folderName, search, limit: 50, page: 1, accessToken: msAuthToken }
      });
      if (res.data && res.data.data) setEmails(res.data.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => {
    fetchEmails(folders.find(f => f.name === selectedFolder).graphName, searchQuery);
    setSelectedEmail(null);
    setEmailDetails(null);
  }, [selectedFolder]);

  const handleSearch = (e) => {
    if (e.key === "Enter") fetchEmails(folders.find(f => f.name === selectedFolder).graphName, searchQuery);
  };

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    setEmailDetails(null);
    try {
      const res = await axios.get(`${API_URL}/api/mail/getEmailDetails`, {
        headers: { authorization: `Bearer ${token}` },
        params: { messageId: email.messageId, accessToken: msAuthToken }
      });
      if (res.data && res.data.success) {
        let bodyHTML = res.data.body;
        if (res.data.attachments && res.data.attachments.length > 0) {
          const inlineAttachments = res.data.attachments.filter(att => att.isInline && att.contentBytes);
          if (inlineAttachments.length > 0) {
            // Match standard cid: tags
            const cidMatches = bodyHTML.match(/cid:([^"'\s>]+)/g);
            if (cidMatches) {
               const uniqueCids = [...new Set(cidMatches)];
               uniqueCids.forEach((cid, index) => {
                 const att = inlineAttachments.find(a => cid.includes(a.name) || (a.contentId && cid.includes(a.contentId))) || inlineAttachments[index] || inlineAttachments[0];
                 if (att) {
                   const regex = new RegExp(cid.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'g');
                   bodyHTML = bodyHTML.replace(regex, `data:${att.contentType};base64,${att.contentBytes}`);
                 }
               });
            } else {
               // Sometimes Graph API returns direct URLs for inline attachments but they are blocked, OR it strips the cid:.
               // Let's just forcefully replace src="..." if we find an inline attachment that we couldn't match, as a fallback,
               // but only if there is exactly 1 inline attachment and 1 image tag.
               const imgTags = bodyHTML.match(/<img[^>]+src=["']([^"']+)["']/gi);
               if (imgTags && imgTags.length === inlineAttachments.length) {
                 inlineAttachments.forEach((att, idx) => {
                    const srcMatch = imgTags[idx].match(/src=["']([^"']+)["']/i);
                    if (srcMatch && srcMatch[1]) {
                        bodyHTML = bodyHTML.replace(srcMatch[1], `data:${att.contentType};base64,${att.contentBytes}`);
                    }
                 });
               }
            }
          }
        }
        
        setEmailDetails({ ...res.data, body: bodyHTML });
        if (!email.isRead) handleMarkAsReadUnread(email.messageId, true);
      }
    } catch (err) { console.error(err); }
  };

  const handleMarkAsReadUnread = async (messageId, isRead) => {
    try {
      await axios.patch(`${API_URL}/api/mail/markAsRead`, { messageId, isRead, accessToken: msAuthToken }, { headers: { authorization: `Bearer ${token}` } });
      setEmails(emails.map(e => e.messageId === messageId ? { ...e, isRead } : e));
      if (selectedEmail?.messageId === messageId) setSelectedEmail({ ...selectedEmail, isRead });
    } catch (err) { console.error(err); }
  };

  const handleToggleFlag = async (messageId, isFlagged) => {
    try {
      await axios.patch(`${API_URL}/api/mail/toggleFlag`, { messageId, isFlagged, accessToken: msAuthToken }, { headers: { authorization: `Bearer ${token}` } });
      setEmails(emails.map(e => e.messageId === messageId ? { ...e, flag: { flagStatus: isFlagged ? "flagged" : "notFlagged" } } : e));
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/api/mail/removeEmail`, { headers: { authorization: `Bearer ${token}` }, data: { messageId, accessToken: msAuthToken } });
      setEmails(emails.filter(e => e.messageId !== messageId));
      if (selectedEmail?.messageId === messageId) setSelectedEmail(null);
    } catch (err) { console.error(err); }
  };

  const openCompose = (type, email = null) => {
    if (type === "new") {
      dispatch(SetMailReplyPayload(null));
    } else if (email) {
      dispatch(SetMailReplyPayload({
        selectedEmail: {
          id: email.messageId,
          from: type === "reply" ? email.from : email.toRecipients?.join(", "),
          subject: type === "forward" ? `Fwd: ${email.subject}` : `Re: ${email.subject}`,
          to: type === "forward" ? "" : (type === "reply" ? email.from : email.toRecipients),
          type: type
        },
        mailBody: emailDetails ? (type === "forward" ? `<br/><br/>---------- Forwarded message ---------<br/><div><b>From:</b> ${email.from}<br/><b>Date:</b> ${new Date(email.receivedDateTime).toLocaleString()}<br/><b>Subject:</b> ${email.subject}</div><br/>${emailDetails.body}` : emailDetails.body) : "",
        attachments: []
      }));
    }
    navigate("/CAE/Sendmail");
  };

  const handleTogglePin = async (messageId, isPinned) => {
    // Optimistic UI update for instant feedback
    setEmails(prev => prev.map(e => e.messageId === messageId ? { ...e, isPinned } : e));
    try {
      await axios.patch(`${API_URL}/api/mail/togglePin`, { messageId, isPinned, accessToken: msAuthToken }, { headers: { authorization: `Bearer ${token}` } });
    } catch (err) { 
      console.error(err); 
      // Revert if backend fails
      setEmails(prev => prev.map(e => e.messageId === messageId ? { ...e, isPinned: !isPinned } : e));
    }
  };

  const formatTime = (dateString) => {
    const d = new Date(dateString);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: '#faf9f8' }}>
      {/* 1. Folders Sidebar
          A plain flex child, not a MUI Drawer. `variant="permanent"` renders its paper
          as `position: fixed` against the viewport, so the pane dropped out of this
          flex row and ended up sitting behind the email list instead of beside it —
          only a sliver of the New mail button and the selected folder stayed visible.
          In normal flow it always occupies its own column. */}
      <Box
        sx={{
          width: isSidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          transition: 'width 0.2s',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          boxSizing: 'border-box',
          bgcolor: '#f3f2f1',
          borderRight: '1px solid #e1dfdd'
        }}
      >
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: isSidebarOpen ? 'flex-end' : 'center' }}>
          <Tooltip title={isSidebarOpen ? "Collapse folders" : "Expand folders"} placement="right">
            <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)} size="small" sx={{ color: '#605e5c' }}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ px: isSidebarOpen ? 2 : 1, pb: 2, display: 'flex', justifyContent: 'center' }}>
          {isSidebarOpen ? (
            <Button
              variant="contained"
              fullWidth
              onClick={() => openCompose("new")}
              sx={{
                bgcolor: '#0f6cbd',
                color: '#fff',
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0c5b9e', boxShadow: 'none' }
              }}
            >
              New mail
            </Button>
          ) : (
            <Tooltip title="New mail" placement="right">
              <IconButton
                onClick={() => openCompose("new")}
                sx={{
                  bgcolor: '#0f6cbd',
                  color: '#fff',
                  borderRadius: '4px',
                  '&:hover': { bgcolor: '#0c5b9e' }
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <List sx={{ px: 1, py: 0 }}>
          {folders.map((folder) => {
            const isSelected = selectedFolder === folder.name;
            return (
              // Collapsed, the icon on its own does not say which folder it is, so
              // the name comes back as a tooltip.
              <Tooltip
                key={folder.name}
                title={isSidebarOpen ? "" : folder.name}
                placement="right"
              >
                <ListItem
                  button
                  onClick={() => setSelectedFolder(folder.name)}
                  sx={{
                    borderRadius: '4px',
                    mb: 0.5,
                    justifyContent: isSidebarOpen ? 'initial' : 'center',
                    px: isSidebarOpen ? 2 : 1,
                    bgcolor: isSelected ? '#e1dfdd' : 'transparent',
                    '&:hover': { bgcolor: isSelected ? '#e1dfdd' : '#edebe9' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: isSidebarOpen ? 36 : 0, justifyContent: 'center', color: isSelected ? '#0f6cbd' : '#605e5c' }}>
                    {folder.icon}
                  </ListItemIcon>
                  {isSidebarOpen && (
                    <ListItemText
                      primary={folder.name}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? '#201f1e' : '#323130',
                        noWrap: true
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* 2. Email List Pane */}
      <Box sx={{
        width: EMAIL_LIST_WIDTH,
        flexShrink: 0,
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#ffffff',
        borderRight: '1px solid #edebe9'
      }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#201f1e', mb: 2 }}>{selectedFolder}</Typography>
          <TextField 
            size="small" 
            fullWidth 
            placeholder="Search" 
            value={searchQuery} 
            onChange={e => setSearchQuery(e.target.value)} 
            onKeyDown={handleSearch}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                bgcolor: '#f3f2f1',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'transparent' },
                '&.Mui-focused fieldset': { borderColor: '#0f6cbd' },
              }
            }}
            InputProps={{ 
              startAdornment: <Search sx={{ color: '#0f6cbd', mr: 1, fontSize: 20 }} /> 
            }}
          />
        </Box>
        <Divider sx={{ borderColor: '#edebe9' }} />
        <List sx={{ flexGrow: 1, overflowY: 'auto', p: 0 }}>
          {loading ? <Typography sx={{ p: 2, color: '#605e5c', fontSize: '14px' }}>Loading...</Typography> : 
            (() => {
              const pinnedList = emails.filter(e => e.isPinned);
              const unpinnedList = emails.filter(e => !e.isPinned);

              const renderEmailItem = (email, isPinnedItem) => {
                const isFlagged = email.flag?.flagStatus === "flagged";
                const isPinned = email.isPinned;
                const isSelected = selectedEmail?.messageId === email.messageId;
                
                return (
                  <ListItem 
                    button 
                    key={email.messageId}
                    onClick={() => handleSelectEmail(email)}
                    sx={{ 
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      p: 0,
                      bgcolor: isSelected ? 'rgba(15, 108, 189, 0.08)' : (isPinnedItem ? '#fffae6' : 'transparent'), // Outlook often tints pinned items slightly yellow
                      borderLeft: isSelected ? '3px solid #0f6cbd' : '3px solid transparent',
                      '&:hover': { bgcolor: isSelected ? 'rgba(15, 108, 189, 0.08)' : '#f3f2f1' },
                      '&:hover .email-date': { display: 'none' },
                      '&:hover .row-actions': { display: 'flex' }
                    }}
                  >
                    <Box sx={{ p: '12px 16px', position: 'relative' }}>
                      {!email.isRead && !isSelected && (
                        <Box sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', width: 8, height: 8, borderRadius: '50%', bgcolor: '#0f6cbd' }} />
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, pl: !email.isRead && !isSelected ? 1.5 : 0 }}>
                        <Typography 
                          sx={{ 
                            fontSize: '14px', 
                            fontWeight: !email.isRead ? 600 : 400,
                            color: '#201f1e',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            pr: 2
                          }}
                        >
                          {selectedFolder === "Sent Items" 
                            ? ((email.to && email.to.length > 0) ? email.to[0] : (email.toRecipients && email.toRecipients.length > 0 ? email.toRecipients[0] : "Unknown Recipient"))
                            : parseSenderName(email.from)}
                        </Typography>
                        
                        {/* Right side: the timestamp swaps out for the row actions on
                            hover. Both stay in normal flow — the old group was absolutely
                            positioned at right:-8, which pushed the second icon past the
                            pane edge where it got clipped. */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0, minHeight: '28px' }}>
                          <Typography className="email-date" sx={{ fontSize: '12px', color: !email.isRead ? '#0f6cbd' : '#605e5c', fontWeight: !email.isRead ? 600 : 400, whiteSpace: 'nowrap' }}>
                            {formatTime(email.receivedDateTime)}
                          </Typography>
                          <Box className="row-actions" sx={{ display: 'none', alignItems: 'center' }}>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDelete(email.messageId); }}
                                sx={{ color: '#605e5c', p: 0.5, '&:hover': { color: '#d13438' } }}
                              >
                                <DeleteOutline sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={email.isRead ? "Mark as unread" : "Mark as read"}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleMarkAsReadUnread(email.messageId, !email.isRead); }}
                                sx={{ color: '#605e5c', p: 0.5, '&:hover': { color: '#0f6cbd' } }}
                              >
                                {email.isRead ? <MarkEmailUnread sx={{ fontSize: 18 }} /> : <MarkEmailRead sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pl: !email.isRead && !isSelected ? 1.5 : 0 }}>
                        <Box sx={{ overflow: 'hidden', pr: 1, width: '100%' }}>
                          <Typography 
                            sx={{ 
                              fontSize: '13.5px', 
                              fontWeight: !email.isRead ? 600 : 400,
                              color: !email.isRead ? '#0f6cbd' : '#323130',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mb: 0.25,
                              textTransform: 'none'
                            }}
                          >
                            {email.subject || '(No Subject)'}
                          </Typography>
                          <Typography 
                            className="email-preview-text"
                            sx={{ 
                              fontSize: '13px', 
                              color: '#605e5c',
                              display: '-webkit-box', 
                              WebkitLineClamp: 1, 
                              WebkitBoxOrient: 'vertical', 
                              overflow: 'hidden',
                              textTransform: 'none'
                            }}
                          >
                            {email.bodyPreview}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0, flexShrink: 0 }}>
                            <Tooltip title={isFlagged ? "Remove flag" : "Flag"}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleToggleFlag(email.messageId, !isFlagged); }}
                                sx={{ color: isFlagged ? '#d13438' : '#605e5c', p: 0.5 }}
                              >
                                {isFlagged ? <Flag sx={{ fontSize: 18 }} /> : <OutlinedFlag sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={isPinned ? "Unpin" : "Pin"}>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleTogglePin(email.messageId, !isPinned); }}
                                sx={{ color: isPinned ? '#0f6cbd' : '#605e5c', p: 0.5 }}
                              >
                                {isPinned ? <PushPin sx={{ fontSize: 18 }} /> : <PushPinOutlined sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Divider sx={{ borderColor: '#edebe9', mx: 2 }} />
                  </ListItem>
                );
              };

              return (
                <>
                  {pinnedList.length > 0 && (
                    <>
                      <ListItem button onClick={() => setPinnedExpanded(!pinnedExpanded)} sx={{ py: 0.5, bgcolor: '#f3f2f1' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#323130', flexGrow: 1 }}>Pinned</Typography>
                        {pinnedExpanded ? <ExpandLess sx={{ fontSize: 16, color: '#605e5c' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#605e5c' }} />}
                      </ListItem>
                      <Collapse in={pinnedExpanded}>
                        {pinnedList.map(email => renderEmailItem(email, true))}
                      </Collapse>
                      {unpinnedList.length > 0 && (
                        <Box sx={{ height: '1px', bgcolor: '#edebe9', my: 1 }} />
                      )}
                    </>
                  )}
                  {unpinnedList.map(email => renderEmailItem(email, false))}
                </>
              );
            })()
          }
        </List>
      </Box>

      {/* 3. Email Detail Pane */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#ffffff', height: '100%' }}>
        {selectedEmail ? (
          <>
            <Toolbar sx={{ minHeight: '52px !important', px: '16px !important', borderBottom: '1px solid #edebe9', gap: 1 }}>
              <IconButton size="small" onClick={() => openCompose("reply", selectedEmail)} title="Reply">
                <Reply fontSize="small" sx={{ color: '#323130' }} />
              </IconButton>
              <IconButton size="small" onClick={() => openCompose("replyAll", selectedEmail)} title="Reply All">
                <ReplyAll fontSize="small" sx={{ color: '#323130' }} />
              </IconButton>
              <IconButton size="small" onClick={() => openCompose("forward", selectedEmail)} title="Forward">
                <Forward fontSize="small" sx={{ color: '#323130' }} />
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 1.5, borderColor: '#edebe9' }} />
              <IconButton size="small" onClick={() => handleMarkAsReadUnread(selectedEmail.messageId, !selectedEmail.isRead)} title={selectedEmail.isRead ? "Mark as unread" : "Mark as read"}>
                {selectedEmail.isRead ? <MarkEmailUnread fontSize="small" sx={{ color: '#323130' }} /> : <MarkEmailRead fontSize="small" sx={{ color: '#323130' }} />}
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(selectedEmail.messageId)} title="Delete">
                <Delete fontSize="small" sx={{ color: '#323130' }} />
              </IconButton>
            </Toolbar>
            <Box sx={{ p: 4, overflowY: 'auto', flexGrow: 1 }}>
              <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#201f1e', mb: 3, textTransform: 'none' }}>
                {selectedEmail.subject}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Avatar sx={{ width: 44, height: 44, mr: 2, bgcolor: 'rgba(15, 108, 189, 0.1)', color: '#0f6cbd', fontSize: '16px', fontWeight: 600 }}>
                  {parseSenderName(selectedEmail.from).charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#201f1e' }}>
                    {parseSenderName(selectedEmail.from)}
                    {selectedEmail.fromEmail && !selectedEmail.fromEmail.includes('/O=EXCHANGELABS') && (
                      <Typography component="span" sx={{ fontSize: '13px', color: '#605e5c', fontWeight: 400, ml: 1 }}>
                        &lt;{selectedEmail.fromEmail}&gt;
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#605e5c' }}>
                    To: {selectedFolder === "Sent Items"
                          ? (selectedEmail.to ? selectedEmail.to.join(", ") : selectedEmail.toRecipients?.join(", "))
                          : "You" + (selectedEmail.toRecipients?.length > 1 ? ` and ${selectedEmail.toRecipients.length - 1} others` : "")
                        }
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '13px', color: '#605e5c' }}>
                  {formatTime(selectedEmail.receivedDateTime)}
                </Typography>
              </Box>
              {emailDetails ? (
                <>
                  {/* Image attachment previews, sized like Outlook: a small
                      thumbnail card capped at ATTACHMENT_PREVIEW_SIZE on its long
                      edge, laid out in a row. Previously these rendered at up to
                      100% wide / 400px tall, so a single attachment filled the
                      whole reading pane. Click downloads the full-size file. */}
                  {emailDetails.attachments && emailDetails.attachments.filter(a => !a.isInline && a.contentType?.startsWith('image/') && a.contentBytes).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                      {emailDetails.attachments.filter(a => !a.isInline && a.contentType?.startsWith('image/') && a.contentBytes).map(att => (
                        <Tooltip key={`preview-${att.id}`} title={att.name}>
                          <Box
                            onClick={() => downloadAttachment(att)}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              border: '1px solid #edebe9',
                              borderRadius: '4px',
                              bgcolor: '#ffffff',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#0f6cbd' }
                            }}
                          >
                            <img
                              src={`data:${att.contentType};base64,${att.contentBytes}`}
                              alt={att.name}
                              style={{
                                maxWidth: ATTACHMENT_PREVIEW_SIZE,
                                maxHeight: ATTACHMENT_PREVIEW_SIZE,
                                objectFit: 'contain',
                                display: 'block'
                              }}
                            />
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                  
                  <div 
                    className="email-body-content"
                    style={{ fontFamily: '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif', color: '#323130', fontSize: '15px', lineHeight: '1.6', textTransform: 'none' }}
                    dangerouslySetInnerHTML={{ __html: emailDetails.body }} 
                  />
                  
                  {/* Attachments Section */}
                  {emailDetails.attachments && emailDetails.attachments.filter(a => !a.isInline).length > 0 && (
                    <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #edebe9' }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#201f1e', mb: 2 }}>
                        Attachments ({emailDetails.attachments.filter(a => !a.isInline).length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {emailDetails.attachments.filter(a => !a.isInline).map(att => (
                          <Box 
                            key={att.id}
                            onClick={() => downloadAttachment(att)}
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              p: 1.5, 
                              border: '1px solid #edebe9', 
                              borderRadius: '4px', 
                              bgcolor: '#f3f2f1', 
                              minWidth: '200px', 
                              maxWidth: '300px',
                              cursor: 'pointer', 
                              '&:hover': { bgcolor: '#e1dfdd' } 
                            }}
                          >
                             <Typography sx={{ fontSize: '13px', color: '#0f6cbd', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                               {att.name}
                             </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Typography sx={{ color: '#605e5c', fontSize: '14px' }}>Loading content...</Typography>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Inbox sx={{ fontSize: 64, color: '#c8c6c4', mb: 2 }} />
              <Typography sx={{ fontSize: '18px', color: '#605e5c' }}>Select an item to read</Typography>
              <Typography sx={{ fontSize: '14px', color: '#a19f9d', mt: 1 }}>Click here to always select the first item in the list</Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function WebmailWrapper() {
  return <ResponsiveDrawer maincontent={<Webmail />} />;
}
